import apiClient from "./api.client.service";
import {
  FileUploadsJobFilters,
  PaginationType,
  ATSCandidate,
} from "../Interface";

class BulkUploadService {
  APIBaseUrl: string;

  constructor() {
    this.APIBaseUrl = process.env.REACT_APP_API_BASE_URL ?? "";
  }

  async getUploadPresignedUrl(
    fileName: string,
    fileType: string,
    jobDescriptionId: string,
  ) {
    const url = `${this.APIBaseUrl}/bulk-upload?fileName=${encodeURIComponent(fileName)}&fileType=${encodeURIComponent(fileType)}&jobDescriptionId=${jobDescriptionId}`;
    console.log("[BulkUpload] getUploadPresignedUrl →", url);
    try {
      const response = await apiClient.get(url);
      console.log(
        "[BulkUpload] getUploadPresignedUrl ✓ presigned URL received:",
        response.data?.url ? "(url present)" : response.data,
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "[BulkUpload] getUploadPresignedUrl ✗ FAILED",
        "\n  status:",
        error?.response?.status,
        "\n  message:",
        error?.response?.data?.message ?? error?.message,
        "\n  full error:",
        error,
      );
      throw error;
    }
  }

  async startFileProcessing(
    jobDescriptionId: string,
    fileName: string,
    screeningSessionId?: string,
  ) {
    const url = `${this.APIBaseUrl}/bulk-upload/${jobDescriptionId}`;
    const body: Record<string, string> = { fileName };
    if (screeningSessionId) body.screeningSessionId = screeningSessionId;
    try {
      const response = await apiClient.post(url, body, {
        headers: { "Content-Type": "application/json" },
      });
      console.log(
        "[BulkUpload] startFileProcessing ✓ response:",
        response?.data,
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "[BulkUpload] startFileProcessing ✗ FAILED",
        "\n  status:",
        error?.response?.status,
        "\n  message:",
        error?.response?.data?.message ?? error?.message,
        "\n  full error:",
        error,
      );
      throw error;
    }
  }

  async startJobCreatingProcessForSingleUpload(
    jobDescriptionId: string,
    fileName: string,
    screeningSessionId?: string,
  ) {
    const url = `${this.APIBaseUrl}/bulk-upload/${jobDescriptionId}/single`;
    const body: Record<string, string> = { fileName };
    if (screeningSessionId) body.screeningSessionId = screeningSessionId;
    try {
      const response = await apiClient.post(url, body, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      console.log(
        "[BulkUpload] startJobCreatingProcessForSingleUpload ✓ response:",
        response?.data,
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ JobDescriptionService ~ getUploadPresignedUrl ~ error:",
        error,
      );
      throw error;
    }
  }

  async getAllBulkUploadsRecords(jobDescriptionId: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/bulk-upload/${jobDescriptionId}`,
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ JobDescriptionService ~ getAllBulkUploads ~ error:",
        error,
      );
      return [];
    }
  }

  async getAllFileUploadsRecords(
    jobDescriptionId: string,
    filter: FileUploadsJobFilters,
    pagination: PaginationType,
  ) {
    const { status, failureReason, sort } = filter;
    try {
      let params = `?jobDescriptionId=${jobDescriptionId}&page=${pagination.page}&limit=${pagination.limit}`;
      if (status) {
        params += `&status=${status}`;
      }
      if (failureReason) {
        params += `&failureReason=${failureReason}`;
      }
      if (sort) {
        params += `&sort=${sort}`;
      }
      const response = await apiClient.get(
        `${this.APIBaseUrl}/bulk-upload/file-uploads/jobs/${params}`,
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "🚀 ~ BulkUploadService ~ getAllFileUploadsRecords ~ error:",
        error,
      );
      return {
        data: [],
        totalPages: 0,
        totalRecords: 0,
      };
    }
  }

  async getAllBulkUploadJobs(
    jobDescriptionId: string,
    bulkUploadId: string,
    filter: FileUploadsJobFilters,
    pagination: PaginationType,
  ) {
    const { status, failureReason, sort } = filter;
    try {
      let params = `?jobDescriptionId=${jobDescriptionId}&bulkUploadId=${bulkUploadId}&page=${pagination.page}&limit=${pagination.limit}`;
      if (status) {
        params += `&status=${status}`;
      }
      if (failureReason) {
        params += `&failureReason=${failureReason}`;
      }
      if (sort) {
        params += `&sort=${sort}`;
      }
      const response = await apiClient.get(
        `${this.APIBaseUrl}/bulk-upload/bulk-uploads/jobs/${params}`,
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "🚀 ~ BulkUploadService ~ getAllBulkUploadJobs ~ error:",
        error,
      );
      return {
        data: [],
        totalPages: 0,
        totalRecords: 0,
      };
    }
  }

  async pullATSCandidates(
    jobDescriptionId: string,
    candidates: ATSCandidate[],
    appType: "zoho" | "lever" | "custom",
  ) {
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/bulk-upload/${jobDescriptionId}/pull`,
        {
          candidates,
          appType,
        },
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "🚀 ~ BulkUploadService ~ pullATSCandidates ~ error:",
        error,
      );
    }
  }
}

const bulkUploadService = new BulkUploadService();
export default bulkUploadService;
